import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import {
    getProjectQASubmissions,
    getProjectSprintQASubmissions,
    getProjectMilestoneQASubmissions,
    getProjectPhaseQASubmissions,
} from '@services/projects.service';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { getTaskDescriptionMarkdown, getSprintDescriptionText } from '@hooks/projects/projectHelpers';

export const PROJECT_QA_SUBMISSIONS_QUERY_KEY = (projectId) => ['projects', projectId, 'qa', 'submissions'];

/**
 * Build sprint columns from submissions: same UI shape as Sprint page (sprint-wise, full sprint + task objects).
 * Merges all submissions into unique sprints by sprint id; tasks per sprint deduplicated by taskId.
 * Supports API shape: sprints[] with id, name, startDate, endDate, status, description; tasks[] with sprintTaskId, sprintId, taskId, title, description, priority, status, sprintName.
 * Used by QA tab (QA submissions) and Completion Requests tab (PMO submissions).
 */
export function buildFlatSprintsFromSubmissions(submissions) {
    if (!Array.isArray(submissions) || submissions.length === 0) return [];

    const sprintMap = new Map();

    submissions.forEach((sub) => {
        const milestoneName = sub.milestoneName ?? sub.milestone?.name ?? null;
        const milestoneObj = sub.milestone ?? (milestoneName ? { name: milestoneName } : null);
        const milestoneId = sub.milestoneId ?? sub.milestone?.id ?? null;

        (sub.sprints || []).forEach((s) => {
            const sprintId = s.id ?? s.sprintId;
            const sprintName = s.name ?? s.sprintName ?? 'Sprint';
            const sprintNumber = s.sprintNumber ?? s.sprint_number ?? null;
            if (!sprintId) return;
            if (!sprintMap.has(sprintId)) {
                const resolvedSprintDesc = resolveDescriptionMentionsForDisplay(getSprintDescriptionText(s), s?.descriptionMentions);
                const sprintDescription =
                    s?.description && typeof s.description === 'object'
                        ? { ...s.description, ...(typeof resolvedSprintDesc === 'string' ? { content: resolvedSprintDesc } : {}) }
                        : (typeof resolvedSprintDesc === 'string' ? resolvedSprintDesc : s?.description);
                sprintMap.set(sprintId, {
                    id: sprintId,
                    name: sprintName,
                    sprintNumber,
                    startDate: s.startDate,
                    endDate: s.endDate,
                    status: s.status,
                    description: sprintDescription,
                    milestoneId,
                    milestone: milestoneObj ? { ...milestoneObj, name: milestoneObj.name ?? milestoneName } : null,
                    tasksMap: new Map(),
                });
            } else {
                const row = sprintMap.get(sprintId);
                if (!row.startDate && s.startDate) row.startDate = s.startDate;
                if (!row.endDate && s.endDate) row.endDate = s.endDate;
                if (row.status == null && s.status != null) row.status = s.status;
                if (!row.description && s.description) row.description = s.description;
                if (row.sprintNumber == null && sprintNumber != null) row.sprintNumber = sprintNumber;
                if (row.milestoneId == null && milestoneId != null) row.milestoneId = milestoneId;
                if (milestoneObj && !row.milestone?.name)
                    row.milestone = { ...milestoneObj, name: milestoneObj.name ?? milestoneName };
            }
        });

        const mergeTaskIntoSprint = (t, sprintId) => {
            const taskId = t.taskId ?? t.id;
            const sprintName =
                sprintId === 'no-sprint'
                    ? 'No Sprint'
                    : t.sprintName ??
                      (sub.sprints || []).find((s) => (s.id ?? s.sprintId) === sprintId)?.name ??
                      (sub.sprints || []).find((s) => (s.id ?? s.sprintId) === sprintId)?.sprintName ??
                      'Sprint';
            if (!sprintId || !taskId) return;

            const resolvedTaskMd = resolveDescriptionMentionsForDisplay(getTaskDescriptionMarkdown(t), t?.descriptionMentions);
            const taskDescription =
                t?.description && typeof t.description === 'object'
                    ? { ...t.description, ...(typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : {}) }
                    : (typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : t?.description);

            const taskForCard = {
                id: t.sprintTaskId ?? t.id ?? taskId,
                sprintTaskId: t.sprintTaskId ?? t.id ?? null,
                taskId,
                taskNumber: t.taskNumber ?? t.task_number ?? null,
                // Backwards-compat: some UI paths read these fields
                displayId: t.taskNumber ?? t.task_number ?? t.displayId ?? t.taskCode ?? t.code ?? null,
                taskCode: t.taskNumber ?? t.task_number ?? t.taskCode ?? null,
                code: t.taskNumber ?? t.task_number ?? t.code ?? null,
                title: t.title ?? t.taskTitle ?? t.name ?? '—',
                name: t.title ?? t.taskTitle ?? t.name ?? '—',
                description: taskDescription,
                priority: t.priority,
                status: t.status,
                type: t.type,
                sprintId,
                submittedAt: t.submittedAt ?? t.submitted_at ?? null,
                qaStatus: t.qaStatus ?? t.qa_status ?? null,
                isBacklog: Boolean(t?.isBacklog ?? t?.is_backlog),
                isChangeRequest: Boolean(t?.isChangeRequest ?? t?.is_change_request),
                activity_logs: Array.isArray(t?.activity_logs)
                    ? t.activity_logs
                    : Array.isArray(t?.activityLogs)
                        ? t.activityLogs
                        : [],
                descriptionMentions: t?.descriptionMentions,
            };

            if (!sprintMap.has(sprintId)) {
                sprintMap.set(sprintId, {
                    id: sprintId,
                    name: sprintName,
                    sprintNumber: null,
                    startDate: null,
                    endDate: null,
                    status: null,
                    description: null,
                    milestoneId,
                    milestone: milestoneObj ? { name: milestoneObj.name ?? milestoneName } : null,
                    tasksMap: new Map(),
                });
            }
            const row = sprintMap.get(sprintId);
            if (!row.tasksMap.has(taskId)) {
                row.tasksMap.set(taskId, taskForCard);
            } else {
                const prev = row.tasksMap.get(taskId);
                row.tasksMap.set(taskId, {
                    ...prev,
                    isBacklog: Boolean(prev.isBacklog || taskForCard.isBacklog),
                    isChangeRequest: Boolean(prev.isChangeRequest || taskForCard.isChangeRequest),
                    status: taskForCard.status ?? prev.status,
                    submittedAt: taskForCard.submittedAt ?? prev.submittedAt,
                    qaStatus: taskForCard.qaStatus ?? prev.qaStatus,
                });
            }
        };

        (sub.tasks || []).forEach((t) => {
            const sprintId = t.sprintId ?? t.sprint?.sprintId ?? 'no-sprint';
            mergeTaskIntoSprint(t, sprintId);
        });

        (sub.sprints || []).forEach((s) => {
            const sid = s.id ?? s.sprintId ?? 'no-sprint';
            if (!sid) return;
            (s.tasks || []).forEach((t) => mergeTaskIntoSprint(t, sid));
        });
    });

    return Array.from(sprintMap.values())
        .filter((s) => s.tasksMap.size > 0)
        .map((s) => ({
            id: s.id,
            name: s.name,
            sprintNumber: s.sprintNumber,
            startDate: s.startDate,
            endDate: s.endDate,
            status: s.status,
            description: s.description,
            milestoneId: s.milestoneId,
            milestone: s.milestone,
            tasks: Array.from(s.tasksMap.values()),
        }));
}

/**
 * Normalise the scoped-endpoint response shape (single sprint object with tasks[] &amp; qaSubmissions[])
 * into the same flat-sprint array the rest of the QA UI expects.
 *
 * Response shape (e.g. GET /sprints/:id/submissions/qa):
 * {
 *   id, name, sprintNumber, phaseId, milestoneId, status, startDate, endDate, description, attachments,
 *   progress: { totalTasks, completedTasks, submittedTasks },
 *   qaSubmissions: [{ submissionId, submittedBy, submittedAt, notes, tasks: [taskId], ... }],
 *   tasks: [{ id, taskId, taskNumber, status, title, description, type, priority, ... }]
 * }
 */
export function buildFlatSprintsFromSprintObject(sprintObj) {
    if (!sprintObj || typeof sprintObj !== 'object') return [];

    const sprintId = sprintObj.id;
    if (!sprintId) return [];

    const tasks = Array.isArray(sprintObj.tasks) ? sprintObj.tasks : [];
    if (tasks.length === 0) return [];

    const resolvedSprintDesc = resolveDescriptionMentionsForDisplay(
        getSprintDescriptionText(sprintObj),
        sprintObj?.descriptionMentions,
    );
    const sprintDescription =
        sprintObj?.description && typeof sprintObj.description === 'object'
            ? { ...sprintObj.description, ...(typeof resolvedSprintDesc === 'string' ? { content: resolvedSprintDesc } : {}) }
            : (typeof resolvedSprintDesc === 'string' ? resolvedSprintDesc : sprintObj?.description);

    const mappedTasks = tasks.map((t) => {
        const taskId = t.taskId ?? t.id;
        const resolvedTaskMd = resolveDescriptionMentionsForDisplay(
            getTaskDescriptionMarkdown(t),
            t?.descriptionMentions,
        );
        const taskDescription =
            t?.description && typeof t.description === 'object'
                ? { ...t.description, ...(typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : {}) }
                : (typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : t?.description);

        return {
            id: t.id ?? taskId,
            sprintTaskId: t.id ?? null,
            taskId,
            taskNumber: t.taskNumber ?? t.task_number ?? null,
            displayId: t.taskNumber ?? t.task_number ?? t.displayId ?? null,
            taskCode: t.taskNumber ?? t.task_number ?? t.taskCode ?? null,
            code: t.taskNumber ?? t.task_number ?? t.code ?? null,
            title: t.title ?? t.taskTitle ?? t.name ?? '—',
            name: t.title ?? t.taskTitle ?? t.name ?? '—',
            description: taskDescription,
            priority: t.priority,
            status: t.status,
            type: t.type,
            sprintId,
            submittedAt: t.submittedAt ?? t.submitted_at ?? null,
            qaStatus: t.qaStatus ?? t.qa_status ?? null,
            isBacklog: Boolean(t?.isBacklog ?? t?.is_backlog),
            isChangeRequest: Boolean(t?.isChangeRequest ?? t?.is_change_request),
            activity_logs: Array.isArray(t?.activity_logs)
                ? t.activity_logs
                : Array.isArray(t?.activityLogs)
                    ? t.activityLogs
                    : [],
            descriptionMentions: t?.descriptionMentions,
        };
    });

    return [{
        id: sprintId,
        name: sprintObj.name ?? 'Sprint',
        sprintNumber: sprintObj.sprintNumber ?? sprintObj.sprint_number ?? null,
        startDate: sprintObj.startDate,
        endDate: sprintObj.endDate,
        status: sprintObj.status,
        description: sprintDescription,
        milestoneId: sprintObj.milestoneId ?? null,
        phaseId: sprintObj.phaseId ?? null,
        milestone: null,
        progress: sprintObj.progress ?? null,
        tasks: mappedTasks,
    }];
}

export function useProjectQASubmissions(projectId, options = {}) {
    const { data: projectFromQuery } = useProjectDetail(projectId, { enabled: !options.project && !!projectId });
    const project = options.project || projectFromQuery;
    const isQAEnabled = project?.capabilities?.submissionMode && project?.capabilities?.submissionMode !== 'none';

    const useSprintsEnabled = project?.capabilities?.useSprints !== false;
    const useMilestonesEnabled = project?.capabilities?.useMilestones !== false;
    const usePhasesEnabled = project?.capabilities?.usePhases !== false;

    const sprintId = options.sprintId;
    const milestoneId = options.milestoneId;

    let mode = 'all';
    let targetId = null;

    // Priority: milestones > phases > sprint-only > all.
    // Milestone/phase endpoints return all sprint+task data for the selected
    // milestone/phase in one call, which populates the sprint tab list.
    // The sprint endpoint requires a selectedSprintId that can only be known
    // AFTER data is loaded, creating a chicken-and-egg deadlock — so we only
    // use it when sprints are enabled but no milestone/phase grouping exists.
    if (useMilestonesEnabled) {
        mode = 'milestone';
        targetId = milestoneId;
    } else if (usePhasesEnabled) {
        mode = 'phase';
        targetId = milestoneId;
    } else if (useSprintsEnabled) {
        mode = 'sprint';
        targetId = sprintId;
    }

    const queryKey = [...PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId), mode, targetId];

    const queryFn = () => {
        if (mode === 'sprint') {
            return getProjectSprintQASubmissions(projectId, targetId);
        }
        if (mode === 'milestone') {
            return getProjectMilestoneQASubmissions(projectId, targetId);
        }
        if (mode === 'phase') {
            return getProjectPhaseQASubmissions(projectId, targetId);
        }
        return getProjectQASubmissions(projectId);
    };

    const isEnabled = Boolean(
        projectId &&
        isQAEnabled &&
        (mode === 'all' || !!targetId)
    );

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey,
        queryFn,
        enabled: isEnabled,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });

    /**
     * Handle two different response shapes:
     *  1. Old shape  (mode === 'all'):  data.data is an array of submission objects
     *  2. New shape  (sprint/phase/milestone scoped): service returns { sprintData: <single object> }
     *     where the sprint object embeds tasks[] directly.
     */
    const flatSprints = useMemo(() => {
        if (!data) return [];
        // New scoped shape: service returns { sprintData }
        if (data.sprintData) {
            return buildFlatSprintsFromSprintObject(data.sprintData);
        }
        // Old shape: data.data is an array of submission wrappers.
        // Only build sprint columns from QA submissions (submittedTo === 'qa')
        // to prevent PMO-only submissions from appearing on the QA board.
        const allSubmissions = Array.isArray(data.data) ? data.data : [];
        const qaOnlySubmissions = allSubmissions.filter(
            (s) => s.submittedTo === 'qa' || s.submissionType === 'qa',
        );
        return buildFlatSprintsFromSubmissions(qaOnlySubmissions);
    }, [data]);

    const submissions = Array.isArray(data?.data)
        ? data.data.filter((s) => s.submittedTo === 'qa' || s.submissionType === 'qa')
        : (data?.sprintData?.qaSubmissions || []);

    const pmoSubmissions = useMemo(() => {
        if (!data) return [];
        if (data.sprintData) {
            return data.sprintData.pmoSubmissions || [];
        }
        const allSubmissions = Array.isArray(data.data) ? data.data : [];
        const explicitPmo = allSubmissions.filter(
            (s) => s.submittedTo === 'pmo' || s.submissionType === 'pmo'
        );
        const extractedPmo = [];
        allSubmissions.forEach((s) => {
            if (s.pmoSubmission && s.pmoSubmission.submissionId) {
                extractedPmo.push({
                    id: s.pmoSubmission.submissionId,
                    milestoneId: s.milestoneId,
                    projectId: s.projectId,
                    submittedBy: s.pmoSubmission.submittedBy,
                    submittedTo: 'pmo',
                    status: s.status,
                    notes: s.pmoSubmission.notes,
                    content: s.pmoSubmission.content,
                    links: s.pmoSubmission.links || [],
                    attachments: s.pmoSubmission.attachments || [],
                    submittedAt: s.pmoSubmission.submittedAt,
                    milestoneName: s.milestoneName,
                    submissionType: s.submissionType,
                    milestone: s.milestone,
                    sprints: s.sprints || [],
                    tasks: s.tasks || [],
                });
            }
        });
        const combined = [...explicitPmo, ...extractedPmo];
        const seen = new Set();
        return combined.filter((sub) => {
            const sid = sub.id;
            if (!sid || seen.has(String(sid))) return false;
            seen.add(String(sid));
            return true;
        });
    }, [data]);

    return {
        submissions,
        pmoSubmissions,
        total: data?.total ?? 0,
        flatSprints,
        isLoading,
        isError,
        refetch,
    };
}
